import type { FC, SyntheticEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Typography, Button, Columns, Column, useToast, Link } from "@aviary";
import { isEmailAtValid, isLengthValid } from "@shared/components/EmailInput";
import { useBreakpoints } from "@shared/hooks";
import { useLocation } from "@shared/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils";
import { useAccountExistsCheck } from "@unauthenticated/shared/hooks";
import { l } from "@unauthenticated/shared/locales/i18n";

import { UnauthorizedErrorBox } from "../UnauthorizedErrorBox";

import { SignInEmailInput } from "./SignInEmailInput";
import { SignInPasswordInput } from "./SignInPasswordInput";
import { useUserSignIn } from "./UserSignIn.mutation";

import * as styles from "./SignInForm.styles";

interface Props {
  additionalAttributes?: any;
  setHasInputErrors?: (value: boolean) => void;
  forgotPasswordLink: string;
  onCompleted?: any;
  isOAuth?: boolean;
  hideExistingAccountCheck?: boolean;
  defaultEmail?: string;
}

const SignInForm: FC<Props> = ({
  additionalAttributes,
  setHasInputErrors,
  forgotPasswordLink,
  onCompleted,
  isOAuth,
  hideExistingAccountCheck,
  defaultEmail,
}) => {
  const search = useLocation().search;
  const urlEmail = new URLSearchParams(search).get("email");
  const { t } = useTranslation();
  const [email, setEmail] = useState(urlEmail || defaultEmail || "");
  const [password, setPassword] = useState("");
  const [isEmailDirty, setIsEmailDirty] = useState(false);
  const [isPasswordDirty, setIsPasswordDirty] = useState(false);
  const [isCredentialsIncorrect, setIsCredentialsIncorrect] = useState(false);
  const [authError, setAuthError] = useState(false);
  const { makeToast } = useToast();
  const { tablet } = useBreakpoints();

  const isEmailAutoFocus = !urlEmail && !defaultEmail && tablet.greaterThan;
  const isPasswordAutoFocus = !isEmailAutoFocus;

  const { isAccountExists, handleCheckIfAccountExists, isAccountCheckLoading } =
    useAccountExistsCheck(true);

  const [signInUser, { loading: isSignInLoading }] = useUserSignIn({
    onCompleted: completedData => {
      const { errors, redirectPath } = completedData?.auth?.userSignIn;

      if (errors?.message?.includes("credentials")) {
        if (setHasInputErrors) setHasInputErrors(true);
        setIsCredentialsIncorrect(true);
      }

      setAuthError(errors?.message?.includes("authorized"));

      if (onCompleted) {
        onCompleted(completedData);
      } else {
        if (redirectPath) {
          window.location.assign(completedData.auth.userSignIn.redirectPath);
        }
      }
    },
    onError: ({ message }) => {
      makeToast("error", message);
    },
  });

  const getPasswordPrompt = () => {
    if (!urlEmail && !defaultEmail) return null;
    return <Typography type="footnote">{t(l.common.PasswordPrompt)}</Typography>;
  };

  const handleEmailOnBlur = e => {
    if (e.target.value) {
      setIsEmailDirty(true);
      setEmail(e.target.value);
    }
    if (isEmailAtValid(e.target.value) && isLengthValid(e.target.value)) {
      handleCheckIfAccountExists(e.target.value);
    }
  };

  const handleEmailOnChange = e => {
    setEmail(e.target.value);
    setIsCredentialsIncorrect(false);
  };

  const handlePasswordOnBlur = e => {
    if (e.target.value) {
      setIsPasswordDirty(true);
    }
  };

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();

    const isEmailValid = isEmailAtValid(email) && isLengthValid(email);
    const isPasswordValid = !!password;
    const isValid = isEmailValid && isPasswordValid && isAccountExists && !isCredentialsIncorrect;

    if (isValid) {
      gRecaptchaExecute(captchaToken => {
        signInUser({
          variables: {
            input: {
              captchaToken,
              attributes: {
                email,
                password,
                optional: additionalAttributes,
              },
            },
          },
        });
      });
    }
    if (hideExistingAccountCheck && !isAccountExists) {
      setIsCredentialsIncorrect(true);
    }
    !isLengthValid(email) ? setIsEmailDirty(true) : setIsPasswordDirty(true);
  };

  const renderForgotPasswordLink = () => {
    const fullLink = `${forgotPasswordLink}/?email=${encodeURIComponent(email)}` as const;

    if (isOAuth) {
      history.pushState({ target: "signin" }, "", window.location.href);
      return (
        <Link css={styles.link} href={fullLink}>
          {t(l.signIn.ForgotPassword)}
        </Link>
      );
    } else {
      return (
        <Link css={styles.link} to={fullLink}>
          {t(l.signIn.ForgotPassword)}
        </Link>
      );
    }
  };

  return (
    <>
      {authError && <UnauthorizedErrorBox />}
      <form noValidate css={styles.form} onSubmit={handleSubmit}>
        <SignInEmailInput
          isLoading={hideExistingAccountCheck ? false : isAccountCheckLoading}
          isAccountExists={hideExistingAccountCheck ?? isAccountExists}
          required
          value={email}
          onChange={handleEmailOnChange}
          isDirty={isEmailDirty}
          onBlur={handleEmailOnBlur}
          descriptiveText={getPasswordPrompt()}
          autoFocus={isEmailAutoFocus}
          wrapperStyles={styles.marginBottom}
        />
        <SignInPasswordInput
          isCredentialsIncorrect={isCredentialsIncorrect}
          required
          onChange={e => {
            setPassword(e.target.value);
            setIsCredentialsIncorrect(false);
          }}
          value={password}
          isDirty={isPasswordDirty}
          onBlur={handlePasswordOnBlur}
          isLoading={false}
          autoFocus={isPasswordAutoFocus}
          wrapperStyles={styles.marginBottom}
          data-e2e="sign-in-password"
        />
        <Columns isMarginless css={styles.row}>
          <Column isPaddingless columnWidth={6}>
            <Typography css={styles.textLeft}>{renderForgotPasswordLink()}</Typography>
          </Column>
          <Column isPaddingless columnWidth={6}>
            <Button isLoading={isSignInLoading} isFullWidth type="submit" data-e2e="sign-in-button">
              {t(l.signIn.SignIn)}
            </Button>
          </Column>
        </Columns>
      </form>
    </>
  );
};

export { SignInForm };
